import { useEffect, useMemo, useState } from 'react'
import { useSearchParams, useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  IconButton, InputAdornment, LinearProgress, MenuItem, Select, Stack, Switch, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AddIcon from '@mui/icons-material/Add'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import { AnimatePresence, motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, type ExportResource, type CompletionReportItem, type ReportTemplate } from '@cmms/api-client'
import type { Site, Location as LocationRecord, Asset, User } from '@cmms/types'
import { formatSiteName } from '../../lib/siteDisplayName'
import type { EmailSignature, Org, OrgPreferences, Role, RoleDetail, Permission, PermissionModule } from '@cmms/types'

import { primary } from '../../theme/palette'
import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useConfirm } from '../../components/ConfirmDialog'
import { useAuthStore } from '../../store/authStore'
import { useAppColorStore } from '../../store/appColorStore'
import { usePermissions } from '../../hooks/usePermissions'
import { useBulkSelection } from '../../hooks/useBulkSelection'
import { buildCompletionReportsZip, buildCombinedCompletionReportsPdf } from '../../lib/bulkReportExport'

import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import { AuditTrailTab } from './AuditTrailTab'

import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined'
import { SlaTab } from './SlaTab'
import { GeofenceTab } from './GeofenceTab'
import ShieldMoonOutlinedIcon from '@mui/icons-material/ShieldMoonOutlined'
import { TrashTab } from './TrashTab'
import { InviteRequestsTab } from './InviteRequestsTab'
import { InvitationsTab } from './InvitationsTab'
import { OrganizationsTab } from './OrganizationsTab'
import { FeedbackTab } from './FeedbackTab'
import { SystemHealthTab } from './SystemHealthTab'
import MarkChatUnreadOutlinedIcon from '@mui/icons-material/MarkChatUnreadOutlined'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import { SubscriptionTab } from './SubscriptionTab'
import { OrgStructureTab } from './OrgStructureTab'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'

import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined'

import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded'
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined'
import { IntegrationsTab } from './IntegrationsTab'
import { InboundEmailTab } from './InboundEmailTab'
import { IssueTemplatesTab } from './IssueTemplatesTab'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { ReportTemplatesTab } from './ReportTemplatesTab'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import { QrTab } from './QrTab'
import { SearchableSelect } from '../../components/SearchableSelect'

import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined'
import { DownloadsTab } from './DownloadsTab'

type TabKey = 'profile' | 'roles' | 'general' | 'prefixes' | 'sla' | 'geofence' | 'org-structure' | 'signatures' | 'notifications' | 'subscription' | 'integrations' | 'inbound-email' | 'qr' | 'issue-templates' | 'report-templates' | 'audit' | 'downloads' | 'export' | 'trash' | 'invite-requests' | 'invitations' | 'organizations' | 'feedback' | 'system-health'

// Old bookmarks/links to the three tabs this replaced (`?tab=customers`,
// `?tab=customer-groups`, `?tab=sites`) land on the merged tab instead
// of silently falling back to the default.
const LEGACY_TAB_ALIASES: Record<string, TabKey> = {
  customers: 'org-structure',
  'customer-groups': 'org-structure',
  sites: 'org-structure',
}

// ─── Constants ──────────────────────────────────────────────────────────────

const CURRENCIES = [
  { code: 'USD', symbol: '$',   label: 'US Dollar' },
  { code: 'EUR', symbol: '€',   label: 'Euro' },
  { code: 'GBP', symbol: '£',   label: 'British Pound' },
  { code: 'INR', symbol: '₹',   label: 'Indian Rupee' },
  { code: 'AUD', symbol: 'A$',  label: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$',  label: 'Canadian Dollar' },
  { code: 'JPY', symbol: '¥',   label: 'Japanese Yen' },
  { code: 'AED', symbol: 'د.إ',  label: 'UAE Dirham' },
  { code: 'SGD', symbol: 'S$',  label: 'Singapore Dollar' },
]

const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY — 04/17/2026' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY — 17/04/2026' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD — 2026-04-17' },
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY — 17 Apr 2026' },
]

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Toronto', 'Europe/London', 'Europe/Berlin', 'Europe/Paris',
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland',
]

// ─── Page ───────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const { can, isSuperAdmin } = usePermissions()
  const user = useAuthStore((s) => s.user)
  // Super admins without an org context need a tab that doesn't require
  // org-scoped permissions — invite-requests is the natural landing.
  const hasOrgContext = !!user?.org_id
  const defaultTab: TabKey = isSuperAdmin && !hasOrgContext
    ? 'invite-requests'
    : 'profile'

  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { siteId } = useParams<{ siteId: string }>()
  // Sites uses path-based routing (/settings/sites/:siteId) so deep
  // links to a specific site survive refresh and can be shared. The
  // other tabs use ?tab=… because they're flat (no nested resources).
  const isSitesPath = location.pathname.startsWith('/settings/sites')
  // The URL is the source of truth so refresh, share-link and browser
  // back/forward all behave. We only `replace` rather than `push` so
  // each tab click doesn't pollute the history stack — the back button
  // still takes you out of /settings, not through every tab you visited.
  const VALID_TABS: TabKey[] = [
    'profile', 'roles', 'general', 'prefixes', 'sla', 'geofence', 'org-structure', 'signatures',
    'notifications', 'subscription', 'integrations', 'inbound-email', 'qr', 'issue-templates',
    'report-templates', 'audit', 'downloads', 'export', 'trash',
    'invite-requests', 'invitations', 'organizations', 'feedback', 'system-health',
  ]
  const rawRequestedTab = searchParams.get('tab')
  const requestedTab = rawRequestedTab
    ? (LEGACY_TAB_ALIASES[rawRequestedTab] ?? (rawRequestedTab as TabKey))
    : null
  const tab: TabKey = isSitesPath
    ? 'org-structure'
    : requestedTab && VALID_TABS.includes(requestedTab)
      ? requestedTab
      : defaultTab
  const setTab = (next: TabKey) => {
    if (next === 'org-structure') {
      navigate('/settings/sites', { replace: true })
      return
    }
    const params = new URLSearchParams()
    params.set('tab', next)
    navigate(`/settings?${params.toString()}`, { replace: true })
  }
  const setSelectedSite = (id: string | null) => {
    navigate(id ? `/settings/sites/${id}` : '/settings/sites', { replace: true })
  }

  const canManageRoles = can('settings:manage_roles')
  const canUpdateSettings = can('settings:update')
  const canManageGeofence = can('work_orders:manage_geofence')
  const canExport = can('settings:export_data')
  const canViewAudit = can('audit:read')
  const canViewOrgStructure = can('customer_groups:read') || can('customers:read') || can('sites:read')
  const canConfigureIntegrations = can('integrations:configure')

  const tabSx = {
    minHeight: 38,
    '& .MuiTab-root': {
      minHeight: 38, textTransform: 'none', fontSize: 13, fontWeight: 600,
      letterSpacing: '-0.005em', color: 'text.secondary', px: 0, mr: 3.5,
      '&.Mui-selected': { color: 'primary.main' },
      '&.Mui-disabled': { opacity: 0.4 },
    },
    '& .MuiTabs-indicator': { height: 2 },
  }

  const toolbar = (
    <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={tabSx}>
      <Tab value="profile" icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Profile" />
      <Tab value="roles" icon={<ShieldOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canManageRoles ? '' : "You don't have permission to manage roles"} arrow><span>Roles & Permissions</span></Tooltip>}
        disabled={!canManageRoles} />
      <Tab value="general" icon={<BusinessOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canUpdateSettings ? '' : "You don't have permission to change org settings"} arrow><span>General</span></Tooltip>}
        disabled={!canUpdateSettings} />
      <Tab value="prefixes" icon={<LabelOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canUpdateSettings ? '' : "You don't have permission to change prefix settings"} arrow><span>Module Prefixes</span></Tooltip>}
        disabled={!canUpdateSettings} />
      <Tab value="sla" icon={<TimerOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canUpdateSettings ? '' : "You don't have permission to manage SLA policies"} arrow><span>SLA Policies</span></Tooltip>}
        disabled={!canUpdateSettings} />
      <Tab value="geofence" icon={<ShieldMoonOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canManageGeofence ? '' : "You don't have permission to configure geofencing"} arrow><span>Geofence</span></Tooltip>}
        disabled={!canManageGeofence} />
      <Tab value="org-structure" icon={<AccountTreeOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canViewOrgStructure ? '' : "You don't have permission to view this"} arrow><span>Org Structure</span></Tooltip>}
        disabled={!canViewOrgStructure} />
      <Tab value="signatures" icon={<DrawOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Signatures" />
      <Tab value="notifications" icon={<NotificationsOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Notifications" />
      <Tab value="subscription" icon={<CreditCardOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Subscription" />
      <Tab value="downloads" icon={<PhoneIphoneOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Mobile Apps" />
      <Tab value="audit" icon={<HistoryOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canViewAudit ? '' : "You don't have permission to view audit logs"} arrow><span>Audit Trail</span></Tooltip>}
        disabled={!canViewAudit} />
      <Tab value="integrations" icon={<HubOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canConfigureIntegrations ? '' : "You don't have permission to configure integrations"} arrow><span>Integrations</span></Tooltip>}
        disabled={!canConfigureIntegrations} />
      <Tab value="inbound-email" icon={<AlternateEmailRoundedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={can('inbound_email:manage') ? '' : "You don't have permission to manage inbound email"} arrow><span>Inbound Email</span></Tooltip>}
        disabled={!can('inbound_email:manage')} />
      <Tab value="issue-templates" icon={<QuestionAnswerOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={can('issue_templates:manage') ? '' : "You don't have permission to manage issue templates"} arrow><span>Issue Templates</span></Tooltip>}
        disabled={!can('issue_templates:manage')} />
      <Tab value="report-templates" icon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={can('report_templates:manage') ? '' : "You don't have permission to manage report templates"} arrow><span>Report Templates</span></Tooltip>}
        disabled={!can('report_templates:manage')} />
      <Tab value="qr" icon={<QrCode2OutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canUpdateSettings ? '' : "You don't have permission to update settings"} arrow><span>QR Branding</span></Tooltip>}
        disabled={!canUpdateSettings} />
      <Tab value="export" icon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canExport ? '' : "You don't have permission to export data"} arrow><span>Data Export</span></Tooltip>}
        disabled={!canExport} />
      {isSuperAdmin && (
        <Tab value="organizations" icon={<DomainOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Organizations" />
      )}
      {isSuperAdmin && (
        <Tab value="invite-requests" icon={<MarkEmailReadOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Invite Requests" />
      )}
      {isSuperAdmin && (
        <Tab value="invitations" icon={<MarkEmailReadOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Invitations" />
      )}
      {isSuperAdmin && (
        <Tab value="trash" icon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Deleted Items" />
      )}
      {isSuperAdmin && (
        <Tab value="feedback" icon={<MarkChatUnreadOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Feedback" />
      )}
      {isSuperAdmin && (
        <Tab value="system-health" icon={<MonitorHeartOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="System Health" />
      )}
    </Tabs>
  )

  return (
    <PageShell title="Settings" toolbar={toolbar} scrollContent={tab !== 'roles' && tab !== 'org-structure'}>
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
          style={tab === 'roles' || tab === 'org-structure' ? { display: 'flex', flex: 1, minHeight: 0 } : {}}
        >
          {tab === 'profile'       && <ProfileTab />}
          {tab === 'roles'         && canManageRoles && <RolesTab />}
          {tab === 'general'       && canUpdateSettings && <GeneralTab />}
          {tab === 'prefixes'      && canUpdateSettings && <PrefixesTab />}
          {tab === 'sla'           && canUpdateSettings && <SlaTab />}
          {tab === 'geofence'      && canManageGeofence && <GeofenceTab />}
          {tab === 'org-structure' && canViewOrgStructure && <OrgStructureTab siteId={siteId ?? null} onSelectSite={setSelectedSite} />}
          {tab === 'signatures'    && <SignaturesTab />}
          {tab === 'notifications' && <NotificationsTab />}
          {tab === 'subscription'  && <SubscriptionTab />}
          {tab === 'downloads'     && <DownloadsTab />}
          {tab === 'audit'         && canViewAudit && <AuditTrailTab />}
          {tab === 'integrations'  && canConfigureIntegrations && <IntegrationsTab />}
          {tab === 'inbound-email' && can('inbound_email:manage') && <InboundEmailTab />}
          {tab === 'issue-templates' && can('issue_templates:manage') && <IssueTemplatesTab />}
          {tab === 'report-templates' && can('report_templates:manage') && <ReportTemplatesTab />}
          {tab === 'qr'            && canUpdateSettings && <QrTab />}
          {tab === 'export'        && canExport && <ExportTab />}
          {tab === 'organizations'   && isSuperAdmin && <OrganizationsTab />}
          {tab === 'invite-requests' && isSuperAdmin && <InviteRequestsTab />}
          {tab === 'invitations'     && isSuperAdmin && <InvitationsTab />}
          {tab === 'trash'           && isSuperAdmin && <TrashTab />}
          {tab === 'feedback'        && isSuperAdmin && <FeedbackTab />}
          {tab === 'system-health'   && isSuperAdmin && <SystemHealthTab />}
        </motion.div>
      </AnimatePresence>
    </PageShell>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// PROFILE TAB
// ═══════════════════════════════════════════════════════════════════════════

const APP_COLOR_PRESETS = [
  { color: '#0D9488', label: 'Teal' },
  { color: '#4F46E5', label: 'Indigo' },
  { color: '#2563EB', label: 'Blue' },
  { color: '#7C3AED', label: 'Violet' },
  { color: '#DB2777', label: 'Pink' },
  { color: '#DC2626', label: 'Red' },
  { color: '#EA580C', label: 'Orange' },
  { color: '#D97706', label: 'Amber' },
  { color: '#059669', label: 'Emerald' },
  { color: '#65A30D', label: 'Lime' },
  { color: '#0284C7', label: 'Sky' },
  { color: '#475569', label: 'Slate' },
]

function ProfileTab() {
  const qc = useQueryClient()
  const me = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const { primaryColor, setPrimaryColor } = useAppColorStore()

  const [name, setName]   = useState(me?.name ?? '')
  const [phone, setPhone] = useState(me?.phone ?? '')
  const [oldPw, setOldPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [customHex, setCustomHex] = useState(primaryColor ?? '')

  useEffect(() => { if (me) { setName(me.name); setPhone(me.phone ?? '') } }, [me])

  const updateM = useMutation({
    mutationFn: () => api.users.updateProfile({ name: name.trim(), phone: phone.trim() || undefined }),
    onSuccess: () => { toast.success('Profile updated'); api.auth.me().then(setUser) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  function handleColorPick(color: string) {
    setPrimaryColor(color)
    setCustomHex(color)
    // Persist to user preferences in DB
    api.users.updateProfile({ preferences: { primaryColor: color } }).catch(() => {})
  }

  function handleResetColor() {
    setPrimaryColor(null)
    setCustomHex('')
    api.users.updateProfile({ preferences: { primaryColor: null } }).catch(() => {})
    toast.success('Reset to default color')
  }

  function handleCustomHexApply() {
    const hex = customHex.trim()
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      handleColorPick(hex)
    } else {
      toast.error('Enter a valid hex color like #FF5722')
    }
  }

  const [showOldPw, setShowOldPw] = useState(false)
  const [showNewPw, setShowNewPw] = useState(false)

  const changePwM = useMutation({
    mutationFn: () => api.auth.changePassword({ current_password: oldPw, new_password: newPw }),
    onSuccess: () => {
      toast.success('Password changed')
      setOldPw(''); setNewPw('')
      setShowOldPw(false); setShowNewPw(false)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const initials = (me?.name ?? '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)

  const [avatarCacheBust, setAvatarCacheBust] = useState(Date.now())

  const uploadAvatarM = useMutation({
    mutationFn: (file: File) => api.users.uploadAvatar(file, (pct) => setUploadProgress(pct)),
    onSuccess: () => { toast.success('Avatar updated'); setUploadProgress(null); setAvatarCacheBust(Date.now()); api.auth.me().then(setUser) },
    onError: (e) => { setUploadProgress(null); toast.error(e instanceof ApiError ? e.message : 'Upload failed') },
  })

  const deleteAvatarM = useMutation({
    mutationFn: () => api.users.deleteAvatar(),
    onSuccess: () => { toast.success('Avatar removed'); setAvatarCacheBust(Date.now()); api.auth.me().then(setUser) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { toast.error('File must be under 5 MB'); return }
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) { toast.error('Only JPEG, PNG, WebP, or GIF allowed'); return }
    uploadAvatarM.mutate(file)
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
      {/* Left — profile info */}
      <Card>
        <CardHeader title="Your profile" subtitle="Manage your name, phone, and avatar." />
        <Stack spacing={3} sx={{ px: 3.5, pb: 3.5 }}>
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                src={me?.avatar_url ? `${me.avatar_url}?t=${avatarCacheBust}` : undefined}
                sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: 24, fontWeight: 700, cursor: 'pointer',
                  transition: 'opacity 150ms', '&:hover': { opacity: 0.8 } }}
                onClick={() => document.getElementById('avatar-upload')?.click()}
              >
                {initials}
              </Avatar>
              {uploadProgress !== null && (
                <CircularProgress variant="determinate" value={uploadProgress} size={78}
                  sx={{ position: 'absolute', top: -3, left: -3, color: 'primary.main' }} />
              )}
              <input id="avatar-upload" type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ display: 'none' }} onChange={handleAvatarChange} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.015em' }}>{me?.name}</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{me?.email}</Typography>
              <Chip label={me?.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())} size="small"
                sx={{ mt: 0.5, height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: 'rgba(13,148,136,0.10)', color: '#0D9488' }} />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => document.getElementById('avatar-upload')?.click()}
                  disabled={uploadAvatarM.isPending}
                  sx={{ fontSize: 11.5, fontWeight: 600, borderRadius: 99, px: 1.5, py: 0.25, textTransform: 'none' }}>
                  {uploadAvatarM.isPending ? 'Uploading...' : 'Change photo'}
                </Button>
                {me?.avatar_url && (
                  <Button size="small" onClick={() => deleteAvatarM.mutate()} disabled={deleteAvatarM.isPending}
                    sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', textTransform: 'none' }}>
                    Remove
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
          <Field label="Full name">
            <TextField value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth sx={inputSx} />
          </Field>
          <Field label="Phone (optional)">
            <TextField value={phone} onChange={(e) => setPhone(e.target.value)} size="small" fullWidth placeholder="+1 555-000-0000" sx={inputSx} />
          </Field>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={() => updateM.mutate()} disabled={updateM.isPending}
              startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
              sx={{ fontSize: 13, fontWeight: 600, px: 2.5 }}>
              {updateM.isPending ? 'Saving…' : 'Save profile'}
            </Button>
          </Box>
        </Stack>
      </Card>

      {/* Right — password */}
      <Card>
        <CardHeader title="Change password" subtitle="Use a strong, unique password." />
        <Stack spacing={3} sx={{ px: 3.5, pb: 3.5 }}>
          <Field label="Current password">
            <TextField type={showOldPw ? 'text' : 'password'} value={oldPw} onChange={(e) => setOldPw(e.target.value)}
              size="small" fullWidth sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowOldPw((v) => !v)}
                      aria-label={showOldPw ? 'Hide password' : 'Show password'}
                      edge="end" tabIndex={-1}>
                      {showOldPw
                        ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                        : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
          </Field>
          <Field label="New password">
            <TextField type={showNewPw ? 'text' : 'password'} value={newPw} onChange={(e) => setNewPw(e.target.value)}
              size="small" fullWidth sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowNewPw((v) => !v)}
                      aria-label={showNewPw ? 'Hide password' : 'Show password'}
                      edge="end" tabIndex={-1}>
                      {showNewPw
                        ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                        : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
          </Field>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => changePwM.mutate()} disabled={!oldPw || !newPw || changePwM.isPending}
              sx={{ fontSize: 13, fontWeight: 600, px: 2.5, color: 'primary.main', borderColor: 'divider' }}>
              {changePwM.isPending ? 'Changing…' : 'Change password'}
            </Button>
          </Box>
        </Stack>
      </Card>

      {/* App color — spans full width */}
      <Card sx={{ gridColumn: { lg: '1 / -1' } }}>
        <CardHeader title="App color" subtitle="Pick a primary color — it changes buttons, links, and accents across the entire app. Saved to your profile." />
        <Stack spacing={3} sx={{ px: 3.5, pb: 3.5 }}>
          {/* Preset swatches */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {APP_COLOR_PRESETS.map((p) => {
              const active = primaryColor === p.color
              return (
                <Tooltip key={p.color} title={p.label} arrow>
                  <Box
                    onClick={() => handleColorPick(p.color)}
                    sx={{
                      width: 40, height: 40, borderRadius: '50%', cursor: 'pointer',
                      border: '3px solid', borderColor: active ? p.color : 'transparent',
                      transition: 'border-color 120ms ease, transform 120ms ease',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      '&:hover': { transform: 'scale(1.1)' },
                    }}
                  >
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '50%', bgcolor: p.color,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {active && (
                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#fff' }} />
                      )}
                    </Box>
                  </Box>
                </Tooltip>
              )
            })}
          </Stack>

          {/* Custom hex input */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{
              width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
              bgcolor: /^#[0-9A-Fa-f]{6}$/.test(customHex) ? customHex : 'action.hover',
              border: '2px solid', borderColor: 'divider',
            }} />
            <TextField
              value={customHex}
              onChange={(e) => setCustomHex(e.target.value)}
              placeholder="#FF5722"
              size="small"
              sx={{ width: 140, '& .MuiOutlinedInput-root': { fontSize: 13, fontFamily: 'ui-monospace, monospace' } }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCustomHexApply() }}
            />
            <Button size="small" onClick={handleCustomHexApply}
              sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main' }}>
              Apply
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={handleResetColor}
              sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary' }}>
              Reset to default
            </Button>
          </Stack>
        </Stack>
      </Card>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// ROLES & PERMISSIONS TAB
// ═══════════════════════════════════════════════════════════════════════════

function RolesTab() {
  const qc = useQueryClient()
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: () => api.roles.list() })
  const roles = Array.isArray(rolesQ.data) ? rolesQ.data : []

  // Auto-select first role
  if (!selectedRoleId && roles.length > 0) setSelectedRoleId(roles[0]!.id)

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', mb: 4 }}>
      {/* Role list */}
      <Box sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, borderRight: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Roles</Typography>
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setCreateOpen(true)}
            sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>New</Button>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1, scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
          '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
          '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>
          {rolesQ.isLoading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
          {roles.map((r) => {
            const sel = r.id === selectedRoleId
            return (
              <Stack key={r.id} direction="row" alignItems="center" spacing={1.25} onClick={() => setSelectedRoleId(r.id)}
                sx={{
                  position: 'relative', px: 2.5, py: 1.25, cursor: 'pointer',
                  transition: 'background-color 120ms ease',
                  bgcolor: sel ? primary.selected : 'transparent',
                  '&:hover': { bgcolor: sel ? primary.selectedHover : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)' },
                  '&::before': sel ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main' } : undefined,
                }}>
                <ShieldOutlinedIcon sx={{ fontSize: 16, color: sel ? 'primary.main' : 'text.secondary' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: sel ? 600 : 500 }} noWrap>{r.name}</Typography>
                    {r.is_system && <LockOutlinedIcon sx={{ fontSize: 11, color: 'text.secondary', opacity: 0.6 }} />}
                  </Stack>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    {r.user_count ?? 0} user{(r.user_count ?? 0) === 1 ? '' : 's'} · {r.permission_count ?? 0} permission{(r.permission_count ?? 0) === 1 ? '' : 's'}
                  </Typography>
                </Box>
              </Stack>
            )
          })}
        </Box>
      </Box>

      {/* Permission editor */}
      {selectedRoleId && <RolePermissionEditor roleId={selectedRoleId} />}

      <CreateRoleDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </Box>
  )
}

function RolePermissionEditor({ roleId }: { roleId: string }) {
  const qc = useQueryClient()
  const roleQ = useQuery({ queryKey: ['role-detail', roleId], queryFn: () => api.roles.get(roleId) })
  const permsQ = useQuery({ queryKey: ['permissions'], queryFn: () => api.permissions.list(), staleTime: 10 * 60 * 1000 })
  const modulesQ = useQuery({ queryKey: ['permission-modules'], queryFn: () => api.permissions.listModules(), staleTime: 10 * 60 * 1000 })

  const role = roleQ.data
  const allPerms = permsQ.data ?? []
  const modules = modulesQ.data ?? []

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (role?.permissions) {
      setSelected(new Set(role.permissions.map((p) => p.id)))
      setDirty(false)
    }
  }, [role])

  const grouped = useMemo(() => {
    const map = new Map<string, Permission[]>()
    allPerms.forEach((p) => {
      const arr = map.get(p.module) ?? []
      arr.push(p)
      map.set(p.module, arr)
    })
    return map
  }, [allPerms])

  function toggle(permId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(permId)) next.delete(permId)
      else next.add(permId)
      return next
    })
    setDirty(true)
  }

  function toggleModule(module: string) {
    const modulePerms = grouped.get(module) ?? []
    const allSelected = modulePerms.every((p) => selected.has(p.id))
    setSelected((prev) => {
      const next = new Set(prev)
      modulePerms.forEach((p) => { if (allSelected) next.delete(p.id); else next.add(p.id) })
      return next
    })
    setDirty(true)
  }

  const saveM = useMutation({
    mutationFn: () => api.roles.update(roleId, { permission_ids: Array.from(selected) }),
    onSuccess: () => {
      toast.success('Permissions saved')
      qc.invalidateQueries({ queryKey: ['role-detail', roleId] })
      qc.invalidateQueries({ queryKey: ['roles'] })
      setDirty(false)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  const deleteM = useMutation({
    mutationFn: () => api.roles.remove(roleId),
    onSuccess: () => {
      toast.success('Role deleted')
      qc.invalidateQueries({ queryKey: ['roles'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  const duplicateM = useMutation({
    mutationFn: () => api.roles.duplicate(roleId),
    onSuccess: (data) => {
      toast.success(`Duplicated as "${data.name}"`)
      qc.invalidateQueries({ queryKey: ['roles'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to duplicate'),
  })

  const [renameOpen, setRenameOpen] = useState(false)
  const [renameName, setRenameName] = useState('')
  const [renameDesc, setRenameDesc] = useState('')

  const renameM = useMutation({
    // Always send `description` (even ''), not `undefined` — the
    // backend's sparse update treats an omitted key as "leave
    // unchanged," so clearing the field would otherwise silently
    // survive a "successful" save.
    mutationFn: () => api.roles.update(roleId, { name: renameName.trim(), description: renameDesc.trim() }),
    onSuccess: () => {
      toast.success('Role renamed')
      qc.invalidateQueries({ queryKey: ['role-detail', roleId] })
      qc.invalidateQueries({ queryKey: ['roles'] })
      setRenameOpen(false)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to rename'),
  })

  const confirmDlg = useConfirm()

  if (roleQ.isLoading || permsQ.isLoading) {
    return <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
  }
  if (!role) return null

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Role header */}
      <Box sx={{ px: 4, pt: 3, pb: 2.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>{role.name}</Typography>
              {role.is_system && <Chip label="System" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: 'action.hover', color: 'text.secondary' }} />}
            </Stack>
            {role.description && <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.35 }}>{role.description}</Typography>}
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
              {role.user_count ?? 0} user{(role.user_count ?? 0) === 1 ? '' : 's'} · {selected.size} / {allPerms.length} permissions
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75}>
            {!role.is_system && (
              <Tooltip title="Rename role" arrow>
                <IconButton size="small" onClick={() => { setRenameName(role.name); setRenameDesc(role.description ?? ''); setRenameOpen(true) }}
                  sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Duplicate role" arrow>
              <IconButton size="small" onClick={() => duplicateM.mutate()}
                disabled={duplicateM.isPending}
                sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
                <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {!role.is_system && (
              <Tooltip title="Delete role" arrow>
                <IconButton size="small" onClick={() => { confirmDlg({ title: `Delete "${role.name}"?`, message: "Users assigned to this role will need a new one.", confirmLabel: "Delete", variant: "danger" }).then((ok) => ok && deleteM.mutate()) }}
                  disabled={deleteM.isPending}
                  sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, '&:hover': { color: 'error.main' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Permissions grid */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3,
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 2.5 }}>
          {Array.from(grouped.entries()).map(([module, perms]) => {
            const allChecked = perms.every((p) => selected.has(p.id))
            const someChecked = perms.some((p) => selected.has(p.id)) && !allChecked
            return (
              <Box key={module} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}
                  sx={{ px: 2.5, py: 1.75, bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Checkbox
                    size="small"
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={() => toggleModule(module)}
                    sx={{ p: 0 }}
                  />
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                    {module.replace(/_/g, ' ')}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                    {perms.filter((p) => selected.has(p.id)).length}/{perms.length}
                  </Typography>
                </Stack>
                <Stack spacing={0} sx={{ px: 1.5, py: 0.75 }}>
                  {perms.map((p) => (
                    <FormControlLabel
                      key={p.id}
                      control={<Checkbox size="small" checked={selected.has(p.id)} onChange={() => toggle(p.id)} sx={{ p: 0.5 }} />}
                      label={
                        <Box sx={{ ml: 0.75 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 500, letterSpacing: '-0.005em' }}>
                            {p.name.split(':')[1]?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) ?? p.name}
                          </Typography>
                          {p.description && (
                            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.35 }}>{p.description}</Typography>
                          )}
                        </Box>
                      }
                      sx={{ mx: 0, py: 0.75, alignItems: 'flex-start' }}
                    />
                  ))}
                </Stack>
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Save bar */}
      <Box sx={{ flexShrink: 0, px: 4, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 12.5, color: dirty ? 'primary.main' : 'text.secondary', fontWeight: 600 }}>
          {dirty ? 'You have unsaved changes' : 'All changes saved'}
        </Typography>
        <Button variant="contained" startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={() => saveM.mutate()} disabled={!dirty || saveM.isPending}
          sx={{ fontSize: 13, fontWeight: 600, px: 3 }}>
          {saveM.isPending ? 'Saving…' : 'Save permissions'}
        </Button>
      </Box>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Rename role</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" required value={renameName} onChange={(e) => setRenameName(e.target.value)}
              size="small" fullWidth autoFocus />
            <TextField label="Description" value={renameDesc} onChange={(e) => setRenameDesc(e.target.value)}
              size="small" fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRenameOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" disabled={!renameName.trim() || renameM.isPending}
            onClick={() => renameM.mutate()}>
            {renameM.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function CreateRoleDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')

  const createM = useMutation({
    mutationFn: () => api.roles.create({ name: name.trim(), description: desc.trim() || undefined }),
    onSuccess: () => { toast.success('Role created'); qc.invalidateQueries({ queryKey: ['roles'] }); setName(''); setDesc(''); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Create role</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Role name" required value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus sx={inputSx} />
          <TextField label="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} size="small" fullWidth multiline minRows={2} sx={inputSx} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={() => createM.mutate()} disabled={!name.trim() || createM.isPending} sx={{ fontSize: 13, fontWeight: 600 }}>
          {createM.isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// GENERAL TAB
// ═══════════════════════════════════════════════════════════════════════════

function GeneralTab() {
  const qc = useQueryClient()
  const orgQ = useQuery({ queryKey: ['org'], queryFn: () => api.orgs.getCurrent() })
  const org = orgQ.data

  const [name, setName] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY')
  const [timeFormat, setTimeFormat] = useState('12h')
  const [timezone, setTimezone] = useState('')

  useEffect(() => {
    if (!org) return
    setName(org.name)
    setCurrency(org.preferences?.currency ?? 'USD')
    setDateFormat(org.preferences?.dateFormat ?? 'MM/DD/YYYY')
    setTimeFormat(org.preferences?.timeFormat ?? '12h')
    setTimezone(org.preferences?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone)
  }, [org])

  const updateM = useMutation({
    mutationFn: () => {
      const cm = CURRENCIES.find((c) => c.code === currency)
      return api.orgs.update({
        name: name.trim(),
        preferences: { currency, currencySymbol: cm?.symbol ?? '$', dateFormat, timeFormat, timezone, language: 'en' },
      })
    },
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries({ queryKey: ['org'] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  if (orgQ.isLoading) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
      <Card>
        <CardHeader title="Organization" subtitle="Your workspace identity." />
        <Stack spacing={3} sx={{ px: 3.5, pb: 3.5 }}>
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Box sx={{ width: 56, height: 56, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: (t) => `${t.palette.primary.main}18`, color: 'primary.main', flexShrink: 0 }}>
              <BusinessOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em' }}>{org?.name}</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontFamily: 'ui-monospace, monospace' }}>{org?.slug}</Typography>
            </Box>
          </Stack>
          <Field label="Organization name">
            <TextField value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth sx={inputSx} />
          </Field>
        </Stack>
      </Card>

      <Card>
        <CardHeader title="Regional preferences" subtitle="Applied across all screens." />
        <Stack spacing={3} sx={{ px: 3.5, pb: 3.5 }}>
          <Field label="Currency">
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)} size="small" fullWidth sx={{ fontSize: 13.5 }}>
              {CURRENCIES.map((c) => <MenuItem key={c.code} value={c.code} sx={{ fontSize: 13 }}>{c.symbol} {c.label} ({c.code})</MenuItem>)}
            </Select>
          </Field>
          <Field label="Date format">
            <Select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} size="small" fullWidth sx={{ fontSize: 13.5 }}>
              {DATE_FORMATS.map((f) => <MenuItem key={f.value} value={f.value} sx={{ fontSize: 13 }}>{f.label}</MenuItem>)}
            </Select>
          </Field>
          <Field label="Time format">
            <Select value={timeFormat} onChange={(e) => setTimeFormat(e.target.value)} size="small" fullWidth sx={{ fontSize: 13.5 }}>
              <MenuItem value="12h" sx={{ fontSize: 13 }}>12-hour (1:30 PM)</MenuItem>
              <MenuItem value="24h" sx={{ fontSize: 13 }}>24-hour (13:30)</MenuItem>
            </Select>
          </Field>
          <Field label="Timezone">
            <SearchableSelect
              value={timezone}
              onChange={setTimezone}
              options={TIMEZONES.map((tz) => ({ value: tz, label: tz.replace(/_/g, ' ') }))}
              getValue={(o) => o.value}
              getLabel={(o) => o.label}
              placeholder="Select a timezone"
            />
          </Field>
        </Stack>
      </Card>

      <Box sx={{ gridColumn: { lg: '1 / -1' }, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={() => updateM.mutate()} disabled={updateM.isPending}
          sx={{ fontSize: 13, fontWeight: 600, px: 3 }}>
          {updateM.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// MODULE PREFIXES TAB
// ═══════════════════════════════════════════════════════════════════════════

const PREFIX_MODULES = [
  { key: 'asset',      label: 'Assets',       icon: '🏗️', example: 'AST',  desc: 'Used in asset codes like AST-00001' },
  { key: 'part',       label: 'Inventory',    icon: '📦', example: 'PRT',  desc: 'Used in part codes like PRT-00001' },
  { key: 'work_order', label: 'Work Orders',  icon: '🔧', example: 'WO',   desc: 'Used in work order codes like WO-00001' },
  { key: 'task',       label: 'Tasks',        icon: '✅', example: 'TSK',  desc: 'Used in task codes like TSK-00001 (soft-services flow)' },
  { key: 'location',   label: 'Locations',    icon: '📍', example: 'LOC',  desc: 'Used in location codes like LOC-00001' },
  { key: 'vendor',     label: 'Vendors',      icon: '🏢', example: 'VND',  desc: 'Used in vendor codes like VND-00001' },
  { key: 'request',    label: 'Requests',     icon: '📋', example: 'REQ',  desc: 'Used in request codes like REQ-00001' },
] as const

function PrefixesTab() {
  const qc = useQueryClient()
  const orgQ = useQuery({ queryKey: ['org'], queryFn: () => api.orgs.getCurrent() })
  const org = orgQ.data

  const [prefixes, setPrefixes] = useState<Record<string, string>>({})
  // Per-org site custom-field label overrides. Blank inputs fall back
  // to "Custom field N" wherever the labels are rendered.
  const [siteFieldLabels, setSiteFieldLabels] = useState<Record<'field1' | 'field2' | 'field3' | 'field4' | 'field5', string>>({
    field1: '', field2: '', field3: '', field4: '', field5: '',
  })

  useEffect(() => {
    if (!org) return
    const saved = (org.preferences as any)?.modulePrefixes ?? {}
    const merged: Record<string, string> = {}
    for (const m of PREFIX_MODULES) merged[m.key] = saved[m.key] ?? m.example
    setPrefixes(merged)

    const labels = (org.preferences as any)?.siteFieldLabels ?? {}
    setSiteFieldLabels({
      field1: labels.field1 ?? '',
      field2: labels.field2 ?? '',
      field3: labels.field3 ?? '',
      field4: labels.field4 ?? '',
      field5: labels.field5 ?? '',
    })
  }, [org])

  const updateM = useMutation({
    mutationFn: () => {
      // Trim empty label overrides to `undefined` so blank inputs
      // don't persist and are re-rendered as the default.
      const cleanLabels: Record<string, string> = {}
      for (const [k, v] of Object.entries(siteFieldLabels)) {
        const t = v.trim()
        if (t.length > 0) cleanLabels[k] = t
      }
      return api.orgs.update({
        preferences: {
          ...(org?.preferences ?? {}),
          modulePrefixes: prefixes,
          siteFieldLabels: cleanLabels,
        } as any,
      })
    },
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries({ queryKey: ['org'] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  if (orgQ.isLoading) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Card>
        <CardHeader title="Module prefixes" subtitle="Configure the prefix tag used when auto-generating codes for each module. Changes apply to newly created items only." />
        <Stack spacing={0} sx={{ px: 3.5, pb: 2 }}>
          {PREFIX_MODULES.map((m) => {
            const val = prefixes[m.key] ?? m.example
            const preview = `${val}-00001`
            return (
              <Stack key={m.key} direction="row" alignItems="center" spacing={2.5}
                sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                <Typography sx={{ fontSize: 22, width: 36, textAlign: 'center', flexShrink: 0 }}>{m.icon}</Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{m.label}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{m.desc}</Typography>
                </Box>
                <TextField
                  value={val}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)
                    setPrefixes((p) => ({ ...p, [m.key]: v }))
                  }}
                  size="small"
                  inputProps={{ style: { textAlign: 'center', fontWeight: 700, fontFamily: 'ui-monospace, monospace', letterSpacing: '0.05em' } }}
                  sx={{ width: 90, '& .MuiOutlinedInput-root': { fontSize: 14 } }}
                />
                <Chip
                  label={preview}
                  size="small"
                  sx={{
                    height: 26, fontSize: 12, fontWeight: 600, fontFamily: 'ui-monospace, monospace',
                    bgcolor: (t) => `${t.palette.primary.main}10`, color: 'primary.main',
                    minWidth: 110, justifyContent: 'center',
                    '& .MuiChip-label': { px: 1.5 },
                  }}
                />
              </Stack>
            )
          })}
        </Stack>
      </Card>

      {/* ── Site custom field labels ─────────────────────────
           Per-org display labels for the 5 generic `field1..5` slots
           on sites. Applies to create / edit dialogs, detail views,
           and site-detail Kv rendering across the app. Blank input =
           the UI reverts to "Custom field N" for that slot. */}
      <Card sx={{ mt: 3 }}>
        <CardHeader
          title="Site custom field labels"
          subtitle="Rename the five generic slots on Sites (Zone, Regional manager, Contract ID, …). Leave blank to fall back to Custom field N."
        />
        <Stack spacing={0} sx={{ px: 3.5, pb: 2 }}>
          {(['field1', 'field2', 'field3', 'field4', 'field5'] as const).map((key, i) => (
            <Stack key={key} direction="row" alignItems="center" spacing={2.5}
              sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
              <Typography sx={{ fontSize: 22, width: 36, textAlign: 'center', flexShrink: 0 }}>#{i + 1}</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                  {siteFieldLabels[key].trim() || `Custom field ${i + 1}`}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                  Slot {i + 1} on every site's <code>{key}</code> column.
                </Typography>
              </Box>
              <TextField
                value={siteFieldLabels[key]}
                onChange={(e) => setSiteFieldLabels((p) => ({ ...p, [key]: e.target.value }))}
                size="small"
                placeholder={`Custom field ${i + 1}`}
                sx={{ width: 260, '& .MuiOutlinedInput-root': { fontSize: 13 } }}
              />
            </Stack>
          ))}
        </Stack>
      </Card>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={() => updateM.mutate()} disabled={updateM.isPending}
          sx={{ fontSize: 13, fontWeight: 600, px: 3 }}>
          {updateM.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// NOTIFICATIONS TAB
// ═══════════════════════════════════════════════════════════════════════════

const NOTIF_PREFS = [
  { label: 'Work order assigned',   desc: 'When a WO is assigned to you or your team.',           key: 'wo_assigned',  def: true },
  { label: 'Work order completed',  desc: 'When a WO you follow is marked done.',                 key: 'wo_completed', def: true },
  { label: 'Work order overdue',    desc: 'When a WO passes its due date.',                       key: 'wo_overdue',   def: true },
  { label: 'New comment',           desc: 'When someone comments on a WO you follow.',            key: 'comment',      def: true },
  { label: 'SLA breach',            desc: 'When a work order breaches its SLA deadline.',         key: 'sla_breach',   def: true },
  { label: 'Request approved',      desc: 'When your maintenance request is approved.',           key: 'request_approved', def: true },
  { label: 'Request declined',      desc: 'When your maintenance request is declined.',           key: 'request_declined', def: true },
  { label: 'Approval needed',       desc: 'When a work order needs your approval.',               key: 'approval_needed', def: true },
  { label: 'Inventory low stock',   desc: 'When a part drops below its reorder point.',           key: 'low_stock',    def: false },
  { label: 'Automation triggered',  desc: 'When an automation fires.',                            key: 'automation',   def: false },
  { label: 'New team member',       desc: 'When someone joins your team.',                        key: 'new_member',   def: true },
  { label: 'Asset status change',   desc: 'When an asset changes to needs-attention or down.',    key: 'asset_status', def: true },
  { label: 'Chat messages',         desc: 'When someone sends you a direct or team message.',     key: 'chat_message', def: true },
]

function NotificationsTab() {
  const qc = useQueryClient()
  const [prefs, setPrefs] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    NOTIF_PREFS.forEach((n) => { init[n.key] = n.def })
    return init
  })
  const [loaded, setLoaded] = useState(false)
  const [dirty, setDirty] = useState(false)

  // Load saved preferences
  const prefsQ = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => api.notifications.getPreferences(),
  })

  useEffect(() => {
    if (prefsQ.data && !loaded) {
      const saved = prefsQ.data as Record<string, boolean>
      const merged: Record<string, boolean> = {}
      NOTIF_PREFS.forEach((n) => { merged[n.key] = saved[n.key] ?? n.def })
      setPrefs(merged)
      setLoaded(true)
    }
  }, [prefsQ.data, loaded])

  const saveM = useMutation({
    mutationFn: () => api.notifications.updatePreferences(prefs),
    onSuccess: () => {
      toast.success('Notification preferences saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['notification-preferences'] })
    },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  function toggle(key: string) {
    setPrefs((p) => ({ ...p, [key]: !p[key] }))
    setDirty(true)
  }

  return (
    <Card sx={{ maxWidth: 640 }}>
      <CardHeader title="Notification preferences" subtitle="Choose which events send you in-app and email notifications." />
      <Stack spacing={0} sx={{ px: 3.5, pb: 3.5 }}>
        {NOTIF_PREFS.map((n, i) => (
          <Stack key={n.key} direction="row" alignItems="center" spacing={2}
            sx={{ py: 2, borderBottom: i < NOTIF_PREFS.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.005em' }}>{n.label}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.15 }}>{n.desc}</Typography>
            </Box>
            <Switch checked={prefs[n.key] ?? false} onChange={() => toggle(n.key)} size="small" />
          </Stack>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 2.5 }}>
          <Typography sx={{ fontSize: 12.5, color: dirty ? 'primary.main' : 'text.secondary', fontWeight: 600 }}>
            {dirty ? 'You have unsaved changes' : 'All changes saved'}
          </Typography>
          <Button variant="contained" startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
            disabled={!dirty || saveM.isPending} onClick={() => saveM.mutate()}
            sx={{ fontSize: 13, fontWeight: 600, px: 3 }}>
            {saveM.isPending ? 'Saving...' : 'Save preferences'}
          </Button>
        </Box>
      </Stack>
    </Card>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// DATA EXPORT TAB
// ═══════════════════════════════════════════════════════════════════════════

const EXPORT_OPTIONS: { label: string; desc: string; icon: React.ElementType; resource: ExportResource }[] = [
  { label: 'Work Orders', desc: 'All WOs with status, assignees, dates.',           icon: BusinessOutlinedIcon,    resource: 'work-orders' },
  { label: 'Tasks',       desc: 'All tasks with status, assignees, actual mins.',   icon: TaskAltOutlinedIcon,     resource: 'tasks' },
  { label: 'Assets',      desc: 'Full asset register with health, cost, location.', icon: BusinessOutlinedIcon,    resource: 'assets' },
  { label: 'Locations',   desc: 'Location hierarchy as a flat table.',              icon: LocationOnOutlinedIcon,  resource: 'locations' },
  { label: 'Org Structure', desc: 'Group, Sub Group, Customer, Site & Location hierarchy — reimportable as-is.', icon: AccountTreeOutlinedIcon, resource: 'org-structure' },
  { label: 'Inventory',   desc: 'Spare parts with stock levels and costs.',         icon: BusinessOutlinedIcon,    resource: 'inventory' },
  { label: 'Users',       desc: 'User roster with roles and site access.',          icon: PersonOutlineIcon,       resource: 'users' },
  { label: 'Teams',       desc: 'Team structure and membership.',                   icon: ShieldOutlinedIcon,      resource: 'teams' },
  { label: 'Requests',    desc: 'Maintenance requests with the WO/task they became.', icon: AssignmentOutlinedIcon, resource: 'requests' },
  { label: 'Checklist responses', desc: 'Every submitted checklist question and answer, linked to its WO/task.', icon: FactCheckOutlinedIcon, resource: 'checklist-responses' },
]

function ExportTab() {
  const [exporting, setExporting] = useState<string | null>(null)

  async function handleExport(label: string, resource: ExportResource) {
    setExporting(label)
    try {
      const { blob, filename } = await api.exports.download(resource)
      // Standard browser-download pattern: object URL → invisible
      // anchor → click → revoke. Has to happen on the same tick as
      // the user gesture for Safari to allow the download.
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success(`${label} exported`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : `Couldn't export ${label.toLowerCase()}`)
    } finally {
      setExporting(null)
    }
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 1180 }}>
      <Card sx={{ maxWidth: 720 }}>
        <CardHeader title="Export data" subtitle="Download CSV files using your org's date and currency format." />
        <Stack spacing={1.5} sx={{ px: 3.5, pb: 3.5 }}>
          {EXPORT_OPTIONS.map((opt) => {
            const Icon = opt.icon
            const isExp = exporting === opt.label
            return (
              <Stack key={opt.label} direction="row" alignItems="center" spacing={2}
                sx={{ px: 2.5, py: 2, borderRadius: 1.75, border: '1px solid', borderColor: 'divider',
                  transition: 'border-color 120ms ease',
                  '&:hover': { borderColor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.16)' } }}>
                <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(15,23,42,0.04)' : 'rgba(255,255,255,0.06)', color: 'text.secondary', flexShrink: 0 }}>
                  <Icon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.005em' }}>{opt.label}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{opt.desc}</Typography>
                </Box>
                <Button variant="outlined" size="small" disabled={isExp} onClick={() => handleExport(opt.label, opt.resource)}
                  startIcon={isExp ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', borderColor: 'divider', flexShrink: 0, minWidth: 110 }}>
                  {isExp ? 'Exporting…' : 'Export CSV'}
                </Button>
              </Stack>
            )
          })}
        </Stack>
      </Card>

      <CompletionReportsExportCard />
    </Stack>
  )
}

function downloadBlob(blob: Blob, filename: string) {
  // Same object-URL → invisible-anchor → click → revoke pattern as the
  // CSV export above — has to run on the same tick as the user gesture
  // for Safari to allow the download.
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

type ReportKindFilter = 'both' | 'work_order' | 'task'
type ReportFormat = 'zip' | 'pdf'

function CompletionReportsExportCard() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [siteId, setSiteId] = useState('')
  const [locationId, setLocationId] = useState('')
  const [assetId, setAssetId] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [kindFilter, setKindFilter] = useState<ReportKindFilter>('both')
  const [format, setFormat] = useState<ReportFormat>('zip')
  // '' = use the org's default template (or the built-in layout if no
  // default is set) — same fallback the single-WO download button and
  // emailed reports already use.
  const [templateId, setTemplateId] = useState('')
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const bulk = useBulkSelection()

  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60_000 })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  // Locations are always fetched scoped to a site (same as the Work
  // Orders filter bar) — there's no "all locations across every site"
  // listing endpoint, so the picker stays disabled until one is chosen.
  const locationsQ = useQuery({
    queryKey: ['locations-for-export', siteId],
    queryFn: () => api.locations.list(siteId),
    enabled: !!siteId,
  })
  const locations: LocationRecord[] = siteId && Array.isArray(locationsQ.data) ? locationsQ.data : []

  const assetsQ = useQuery({
    queryKey: ['assets-for-export', siteId],
    queryFn: () => api.assets.list(siteId ? { site_id: siteId } : undefined),
    staleTime: 60_000,
  })
  const assets: Asset[] = Array.isArray(assetsQ.data) ? assetsQ.data : []

  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => api.users.list(), staleTime: 5 * 60_000 })
  const users: User[] = Array.isArray(usersQ.data) ? usersQ.data : []

  const templatesQ = useQuery({
    queryKey: ['report-templates'],
    queryFn: () => api.reportTemplates.list(),
    staleTime: 60_000,
  })
  const reportTemplates: ReportTemplate[] = Array.isArray(templatesQ.data) ? templatesQ.data : []

  // Reactive, not button-driven — every filter change refetches
  // automatically (react-query keys off the tuple below), including
  // the very first render with no filters at all, which is what makes
  // "show everything, then narrow" work.
  const itemsQ = useQuery({
    queryKey: ['completion-reports', from, to, siteId, locationId, assetId, assignedTo, kindFilter],
    queryFn: () => api.exports.listCompletionReports({
      from: from || undefined,
      to: to || undefined,
      site_id: siteId || undefined,
      location_id: locationId || undefined,
      asset_id: assetId || undefined,
      assigned_to: assignedTo || undefined,
      kind: kindFilter === 'both' ? undefined : kindFilter,
    }),
    placeholderData: (prev) => prev,
  })
  const items: CompletionReportItem[] = Array.isArray(itemsQ.data) ? itemsQ.data : []
  const allIds = useMemo(() => items.map((i) => i.id), [items])

  // New result set in — default to everything selected, same as before.
  useEffect(() => { bulk.replaceWith(allIds) }, [allIds])

  function pickSite(v: string) {
    setSiteId(v)
    setLocationId('')
  }

  async function handleDownload() {
    const selectedItems = items.filter((it) => bulk.isSelected(it.id))
    if (selectedItems.length === 0) { toast.error('Select at least one report'); return }
    setBusy(true)
    setProgress(null)
    try {
      // Work orders and tasks are gated by separate permissions
      // (work_orders:read vs tasks:read) even though they share the
      // same underlying table — route each item through its own
      // kind's endpoint rather than always hitting /work-orders.
      const byId = new Map(selectedItems.map((it) => [it.id, it.kind]))
      const fetchPdfBytes = (id: string) =>
        byId.get(id) === 'task'
          ? api.tasks.downloadReportPdf(id, templateId || undefined)
          : api.workOrders.downloadReportPdf(id, templateId || undefined)
      const result = format === 'zip'
        ? await buildCompletionReportsZip(selectedItems, fetchPdfBytes, setProgress)
        : await buildCombinedCompletionReportsPdf(selectedItems, fetchPdfBytes, setProgress)

      const ext = format === 'zip' ? 'zip' : 'pdf'
      const stamp = from || to ? `_${from || 'start'}_to_${to || 'now'}` : ''
      downloadBlob(result.blob, `completion-reports${stamp}.${ext}`)

      if (result.failed.length > 0) {
        // Surface the first failure's real reason (a timeout, a 404 for
        // a deleted record, a 500) instead of just a bare count — a
        // silent "failed to render" gives no way to tell "this record
        // is gone" from "the render is slow, try again" apart.
        const reason = result.failed[0]?.error
        const suffix = reason ? ` — ${reason}` : ''
        toast.error(`${result.succeeded} downloaded, ${result.failed.length} failed to render${suffix}`)
      } else {
        toast.success(`${result.succeeded} completion report${result.succeeded === 1 ? '' : 's'} downloaded`)
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Couldn't build the export")
    } finally {
      setBusy(false)
      setProgress(null)
    }
  }

  return (
    <Card>
      <CardHeader title="Completion reports"
        subtitle="Bulk-download the same Work Order / Task completion reports that get emailed on resolution." />
      <Stack spacing={2.5} sx={{ px: 3.5, pb: 3.5 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ '& > *': { width: 180 } }}>
          <TextField label="From" type="date" size="small" value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} disabled={busy} />
          <TextField label="To" type="date" size="small" value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }} disabled={busy} />
          <SearchableSelect value={siteId} onChange={pickSite} options={sites}
            getValue={(s) => s.id} getLabel={formatSiteName} placeholder="Site" disabled={busy} />
          <SearchableSelect value={locationId} onChange={setLocationId} options={locations}
            getValue={(l) => l.id} getLabel={(l) => l.name}
            placeholder={siteId ? 'Location' : 'Pick site first'} disabled={busy || !siteId}
            loading={!!siteId && locationsQ.isLoading} />
          <SearchableSelect value={assetId} onChange={setAssetId} options={assets}
            getValue={(a) => a.id} getLabel={(a) => a.name} placeholder="Asset" disabled={busy}
            loading={assetsQ.isLoading} />
          <SearchableSelect value={assignedTo} onChange={setAssignedTo} options={users}
            getValue={(u) => u.id} getLabel={(u) => u.name} placeholder="Assigned to (people)" disabled={busy}
            loading={usersQ.isLoading} />
        </Stack>

        <ToggleButtonGroup exclusive size="small" value={kindFilter} disabled={busy}
          onChange={(_, v) => { if (v) setKindFilter(v) }}>
          <ToggleButton value="both" sx={{ fontSize: 12.5, fontWeight: 600, px: 2 }}>Both</ToggleButton>
          <ToggleButton value="work_order" sx={{ fontSize: 12.5, fontWeight: 600, px: 2 }}>Work Orders</ToggleButton>
          <ToggleButton value="task" sx={{ fontSize: 12.5, fontWeight: 600, px: 2 }}>Tasks</ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary' }}>
            {itemsQ.isFetching ? 'Loading…' : `${bulk.count} of ${items.length} selected`}
          </Typography>
          {itemsQ.isFetching && <CircularProgress size={13} />}
        </Stack>

        <TableContainer sx={{ maxHeight: 420, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={bulk.allSelected(allIds)} indeterminate={bulk.someSelected(allIds)}
                    onChange={(e) => bulk.replaceWith(e.target.checked ? allIds : [])} disabled={busy} />
                </TableCell>
                <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>Code</TableCell>
                <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>Title</TableCell>
                <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>Site</TableCell>
                <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>Location</TableCell>
                <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>Asset</TableCell>
                <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>Assigned to</TableCell>
                <TableCell sx={{ fontSize: 11.5, fontWeight: 700 }}>Resolved</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} hover selected={bulk.isSelected(it.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={bulk.isSelected(it.id)} onChange={() => bulk.toggle(it.id)} disabled={busy} />
                  </TableCell>
                  <TableCell>
                    <Chip label={it.kind === 'task' ? 'Task' : 'WO'} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' }}>{it.code ?? '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Typography noWrap sx={{ fontSize: 13 }}>{it.title}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{it.site_name ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{it.location_name ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{it.asset_name ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{it.assigned_to_name ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{new Date(it.resolved_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {items.length === 0 && !itemsQ.isFetching && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, fontSize: 13, color: 'text.secondary' }}>
                    No completed work orders/tasks match these filters
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <ToggleButtonGroup exclusive size="small" value={format} disabled={busy}
          onChange={(_, v) => { if (v) setFormat(v) }}>
          <ToggleButton value="zip" sx={{ fontSize: 12.5, fontWeight: 600, px: 2 }}>ZIP (one folder per date)</ToggleButton>
          <ToggleButton value="pdf" sx={{ fontSize: 12.5, fontWeight: 600, px: 2 }}>Single combined PDF</ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <SearchableSelect value={templateId} onChange={setTemplateId} options={reportTemplates}
            getValue={(t) => t.id} getLabel={(t) => t.name}
            placeholder="Report template (org default)" disabled={busy}
            loading={templatesQ.isLoading} />
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
            {templateId
              ? 'Applies to every report in this export.'
              : reportTemplates.some((t) => t.is_default)
                ? `Using the org default (${reportTemplates.find((t) => t.is_default)?.name}).`
                : 'No default set — using the built-in layout. Manage templates under Settings → Report Templates.'}
          </Typography>
        </Stack>

        {progress && (
          <Stack spacing={0.75}>
            <LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} sx={{ borderRadius: 99, height: 6 }} />
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Rendering {progress.done} of {progress.total}…
            </Typography>
          </Stack>
        )}

        <Button variant="contained" disabled={busy || bulk.count === 0} onClick={handleDownload}
          startIcon={busy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{ fontSize: 13, fontWeight: 600, alignSelf: 'flex-start' }}>
          {busy ? 'Generating…' : `Download selected (${bulk.count})`}
        </Button>
      </Stack>
    </Card>
  )
}

// ─── Shared building blocks ─────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// SIGNATURES TAB
// ═══════════════════════════════════════════════════════════════════════════

function SignaturesTab() {
  const { user, setUser } = useAuthStore()
  const prefs = (user?.preferences ?? {}) as Record<string, any>
  const signatures: EmailSignature[] = Array.isArray(prefs.signatures) ? prefs.signatures : []

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editBody, setEditBody] = useState('')
  const [creating, setCreating] = useState(false)

  const saveM = useMutation({
    mutationFn: async (updated: EmailSignature[]) => {
      return api.users.updateProfile({ preferences: { ...prefs, signatures: updated } } as any)
    },
    onSuccess: async () => {
      const me = await api.auth.me()
      setUser(me)
      toast.success('Signatures saved')
      setEditingId(null)
      setCreating(false)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  function handleAdd() {
    setCreating(true)
    setEditName('')
    setEditBody('')
  }

  function handleSaveNew() {
    const sig: EmailSignature = { id: crypto.randomUUID(), name: editName.trim(), body: editBody.trim() }
    saveM.mutate([...signatures, sig])
  }

  function handleEdit(sig: EmailSignature) {
    setEditingId(sig.id)
    setEditName(sig.name)
    setEditBody(sig.body)
    setCreating(false)
  }

  function handleSaveEdit() {
    const updated = signatures.map((s) => s.id === editingId ? { ...s, name: editName.trim(), body: editBody.trim() } : s)
    saveM.mutate(updated)
  }

  function handleDelete(id: string) {
    saveM.mutate(signatures.filter((s) => s.id !== id))
  }

  function handleSetDefault(id: string) {
    const updated = signatures.map((s) => ({ ...s, isDefault: s.id === id }))
    saveM.mutate(updated)
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Card>
        <CardHeader title="Email signatures" subtitle="Create signatures to use when sending emails from the app (e.g. reorder requests). Your default signature will be pre-selected." />
        <Box sx={{ px: 3.5, pb: 3.5 }}>
          {signatures.length === 0 && !creating && (
            <Box sx={{ py: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, mb: 2 }}>
              <DrawOutlinedIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>No signatures yet</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>Create your first email signature to attach to outgoing emails.</Typography>
            </Box>
          )}

          <Stack spacing={2}>
            {signatures.map((sig) => (
              editingId === sig.id ? (
                <Box key={sig.id} sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 2, p: 2.5 }}>
                  <TextField label="Signature name" value={editName} onChange={(e) => setEditName(e.target.value)}
                    size="small" fullWidth placeholder="e.g. Work, Formal" sx={{ mb: 2 }} />
                  <TextField label="Signature content" value={editBody} onChange={(e) => setEditBody(e.target.value)}
                    size="small" fullWidth multiline minRows={4}
                    placeholder={'Best regards,\nJohn Doe\nMaintenance Manager\nBacsys Innovations\n+91 99623 03968'} />
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button variant="contained" size="small" disabled={!editName.trim() || !editBody.trim() || saveM.isPending}
                      onClick={handleSaveEdit} sx={{ fontSize: 12.5 }}>
                      {saveM.isPending ? 'Saving...' : 'Save'}
                    </Button>
                    <Button size="small" onClick={() => setEditingId(null)} sx={{ fontSize: 12.5, color: 'text.secondary' }}>Cancel</Button>
                  </Stack>
                </Box>
              ) : (
                <Box key={sig.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.main' } }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{sig.name}</Typography>
                      {sig.isDefault && <Chip label="Default" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: (t) => `${t.palette.primary.main}18`, color: 'primary.main' }} />}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      {!sig.isDefault && (
                        <Tooltip title="Set as default signature" arrow>
                          <IconButton size="small" onClick={() => handleSetDefault(sig.id)} sx={{ color: 'text.secondary' }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit" arrow>
                        <IconButton size="small" onClick={() => handleEdit(sig)} sx={{ color: 'text.secondary' }}>
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton size="small" onClick={() => handleDelete(sig.id)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{sig.body}</Typography>
                </Box>
              )
            ))}

            {creating && (
              <Box sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 2, p: 2.5 }}>
                <TextField label="Signature name" value={editName} onChange={(e) => setEditName(e.target.value)}
                  size="small" fullWidth autoFocus placeholder="e.g. Work, Formal" sx={{ mb: 2 }} />
                <TextField label="Signature content" value={editBody} onChange={(e) => setEditBody(e.target.value)}
                  size="small" fullWidth multiline minRows={4}
                  placeholder={'Best regards,\nJohn Doe\nMaintenance Manager\nBacsys Innovations\n+91 99623 03968'}
                  helperText="This will appear at the bottom of your outgoing emails" />
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button variant="contained" size="small" disabled={!editName.trim() || !editBody.trim() || saveM.isPending}
                    onClick={handleSaveNew} sx={{ fontSize: 12.5 }}>
                    {saveM.isPending ? 'Creating...' : 'Create signature'}
                  </Button>
                  <Button size="small" onClick={() => setCreating(false)} sx={{ fontSize: 12.5, color: 'text.secondary' }}>Cancel</Button>
                </Stack>
              </Box>
            )}
          </Stack>

          {!creating && !editingId && (
            <Button startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={handleAdd}
              sx={{ mt: 2, fontSize: 13, fontWeight: 600, color: 'primary.main' }}>
              Add signature
            </Button>
          )}
        </Box>
      </Card>
    </Box>
  )
}

// ─── Shared helpers ───────────────────────────────────────────────────────

function Card({ children, sx, ...props }: React.ComponentProps<typeof Box>) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', overflow: 'hidden', ...sx }} {...props}>
      {children}
    </Box>
  )
}

function CardHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Box sx={{ px: 3.5, pt: 3, pb: 2.5 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em' }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.35 }}>{subtitle}</Typography>}
    </Box>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.85 }}>
        {label}
      </Typography>
      {children}
    </Box>
  )
}

const inputSx = { '& .MuiOutlinedInput-root': { fontSize: 13.5 } }
